import React from 'react';
import {
  Table2,
  Paperclip,
  Code,
  Mail,
  Webhook,
  BarChart3,
  Settings,
  Layers,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { InnerLayout, PillNavItem } from '../components/layout/InnerLayout';
import { HedgehogSpinner } from '../components/brand/HedgehogSpinner';
import { useTableViewState, WorkspaceTab } from './table/hooks/useTableViewState';
import { TableEmptyState } from './table/components/TableEmptyState';
import { TableTabContent } from './table/components/TableTabContent';

const mobileWorkspaceTabs: Array<{
  id: WorkspaceTab;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}> = [
  { id: 'data', label: 'Data', icon: Table2 },
  { id: 'files', label: 'Files', icon: Paperclip },
  { id: 'schema', label: 'Schema', icon: Layers },
  { id: 'endpoints', label: 'Endpoints', icon: Code },
  { id: 'rules', label: 'Rules', icon: Mail },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  { id: 'spam', label: 'Spam', icon: ShieldAlert },
  { id: 'analytics', label: 'Telemetry', icon: BarChart3 },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export const TableView: React.FC = () => {
  const {
    currentProject,
    tables,
    activeTable,
    setActiveTable,
    setCurrentTable,
    isTablesLoading,
    permissionTier,
    canEdit,
    isFullTier,
    headerActionEl,
    setHeaderActionEl,
    activeTab,
    handleTabChange,
    settingsSubTab,
    setSettingsSubTab,
    columns,
    setColumns,
    rows,
    totalRows,
    page,
    setPage,
    pageSize,
    handlePageSizeChange,
    isLoading,
    isRefreshing,
    filterRules,
    handleFilterChange,
    unfilteredTotal,
    sortCol,
    sortDir,
    sortRules,
    handleSortChange,
    handleEditingChange,
    handleServerSearchChange,
    fetchRows,
    resolvedTable,
    paramTableId,
  } = useTableViewState();

  if (
    isTablesLoading ||
    (paramTableId && !activeTable && tables.length === 0) ||
    (isLoading && rows.length === 0 && !activeTable && tables.length > 0)
  ) {
    return (
      <div className="flex items-center justify-center h-96">
        <HedgehogSpinner size="lg" label="LOADING DATASTORE WORKSPACE..." />
      </div>
    );
  }

  if (!currentProject || (!activeTable && tables.length === 0)) {
    return <TableEmptyState />;
  }

  const getHeaderDetails = () => {
    switch (activeTab) {
      case 'data':
        return {
          title: (
            <>
              <span>{resolvedTable?.name || 'Datastore'}</span>
              {resolvedTable?.is_active !== false ? (
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" title="Live" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-zinc-500 shrink-0" title="Paused" />
              )}
            </>
          ),
          description:
            resolvedTable?.description || 'Fast form ingestion and relational records storage',
        };
      case 'files':
        return {
          title: `Uploaded Files & Attachments for ${resolvedTable?.name || 'Datastore'}`,
          description:
            'Browse, preview, and download submitted file attachments securely with expiring HMAC-signed gateway links.',
        };
      case 'schema':
        return {
          title: 'Datastore Schema & Column Definitions',
          description:
            'Define column data types for data grid rendering, filtering, and configure strict vs dynamic schema evaluation modes.',
        };
      case 'endpoints':
        return {
          title: 'Ingress Endpoints & Code Snippets',
          description:
            'Connect your web forms, applications, and HTTP clients directly to this datastore with instant acknowledgment.',
        };
      case 'rules':
        return {
          title: 'Notification Rules',
          description:
            'Trigger automatic email dispatches when records are ingested into this datastore.',
        };
      case 'webhooks':
        return {
          title: 'Webhooks',
          description:
            'Send real-time HTTP POST notifications with HMAC signatures to external URLs whenever records are submitted to this datastore.',
        };
      case 'spam':
        return {
          title: 'Ingestion Security, Allowed Origins & Spam Traps',
          description:
            'Configure allowed hostnames, rate limiting, and hidden honeypot fields to silently quarantine malicious bot submissions.',
        };
      case 'analytics':
        return {
          title: `Operational Telemetry for ${resolvedTable?.name || 'Datastore'}`,
          description:
            'Ingestion throughput, spam quarantine metrics, and latency percentiles scoped to this datastore.',
        };
      case 'settings':
        return {
          title: 'Datastore Identification & Settings',
          description:
            'Manage datastore identification, description, and permanent deletion controls.',
        };
      default:
        return {
          title: resolvedTable?.name || 'Datastore',
          description: resolvedTable?.description || '',
        };
    }
  };

  const headerDetails = getHeaderDetails();

  const navItems: PillNavItem[] | undefined =
    activeTab === 'settings'
      ? [
          {
            id: 'general',
            label: 'General',
            active: settingsSubTab === 'general',
            onClick: () => setSettingsSubTab('general'),
          },
          ...(isFullTier
            ? [
                {
                  id: 'danger',
                  label: 'Danger Zone',
                  icon: Trash2,
                  danger: true,
                  active: settingsSubTab === 'danger',
                  onClick: () => setSettingsSubTab('danger'),
                },
              ]
            : []),
        ]
      : mobileWorkspaceTabs.map((t) => ({
          id: t.id,
          label: t.label,
          icon: t.icon,
          active: activeTab === t.id,
          onClick: () => handleTabChange(t.id),
          className: 'md:hidden',
        }));

  return (
    <InnerLayout
      header={{
        title: headerDetails.title,
        description: headerDetails.description,
        actions: <div ref={setHeaderActionEl} className="flex items-center gap-2 empty:hidden" />,
      }}
      navItems={navItems}
      variant={activeTab === 'data' ? 'fullHeight' : 'default'}
    >
      <TableTabContent
        activeTab={activeTab}
        resolvedTable={resolvedTable}
        currentProject={currentProject}
        columns={columns}
        rows={rows}
        totalRows={totalRows}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        onRowsUpdated={() => fetchRows(true, false)}
        onRefresh={() => fetchRows(true, true)}
        isRefreshing={isRefreshing}
        permissionTier={permissionTier}
        isLoading={isLoading}
        canEdit={canEdit}
        isFullTier={isFullTier}
        onEditingChange={handleEditingChange}
        onServerSearchChange={handleServerSearchChange}
        filterRules={filterRules}
        onFilterChange={handleFilterChange}
        unfilteredTotal={unfilteredTotal}
        sortCol={sortCol}
        sortDir={sortDir}
        sortRules={sortRules}
        onSortChange={handleSortChange}
        onNavigateEndpoints={() => handleTabChange('endpoints')}
        setColumns={setColumns}
        onTableUpdated={(updated) => {
          setActiveTable(updated);
          setCurrentTable(updated);
        }}
        settingsSubTab={settingsSubTab}
        onSettingsSubTabChange={setSettingsSubTab}
        headerActionEl={headerActionEl}
      />
    </InnerLayout>
  );
};
